/* eslint-disable no-restricted-globals */
import { Button, IllustratedMessage, Progress, Stack } from '@eleveo/components-react';
import { PlusIcon, SearchNoneIcon } from '@eleveo/icons-react';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { Center } from '../../components/LayoutNext/components/Center';
import { ModalConfirmationNewStyle } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  GetSkillsAllWithUsedDocument,
  GetSkillsAllWithUsedQuery,
  Skill,
  SkillInput,
  SkillWithUsed,
  UpdateSkillMutationVariables,
  useCreateSkillMutation,
  useDeleteSkillMutation,
  useGetSkillsAllWithUsedQuery,
  useUpdateSkillMutation,
} from '../../graphql/components';
import { SkillsSidebar } from './SkillsSidebar';
import { SkillsTable } from './SkillsTable';
import { ADD_SKILL_BUTTON_TAG, CONTAINER_TAG, TITLE_TAG } from './constants';

export const SkillsHeader: React.FC<{
  t: TFunction;
  handleCreate: () => void;
  isCreateButtonActive: boolean;
}> = ({ t, handleCreate, isCreateButtonActive }) => {
  const { userHasPermissions } = useAuth();

  return userHasPermissions([Permissions.CreateSkill]) ? (
    <Stack direction="horizontal" className="w-full">
      <Button color="info" isSelected={isCreateButtonActive} onClick={handleCreate} testId={ADD_SKILL_BUTTON_TAG}>
        <PlusIcon />
        {t('skill.add')}
      </Button>
    </Stack>
  ) : null;
};

export type Values = Pick<Skill, 'id' | 'name'>;

export const createSkillFromValues = (values: Values): SkillInput => ({
  name: values.name,
});

export const SkillsScene: React.FC = () => {
  const { openSidebar, closeSidebar, toggleSidebar, isSidebarOpen } = useSidebar();
  const { t } = useTranslation();

  const [skillToEdit, setSkillToEdit] = useState<SkillWithUsed | null>(null);
  const [skillToDelete, setSkillToDelete] = useState<SkillWithUsed | null>(null);

  const {
    data: skillsData,
    loading: skillsLoading,
    error: skillsError,
  } = useGetSkillsAllWithUsedQuery({
    fetchPolicy: 'network-only',
  });

  const [createSkill, { loading: createSkillLoading }] = useCreateSkillMutation();
  const [updateSkill, { loading: updateSkillLoading }] = useUpdateSkillMutation();
  const [deleteSkill, { loading: deleteSkillLoading }] = useDeleteSkillMutation();

  const skills: SkillWithUsed[] = skillsData?.skillsWithUsed ?? [];

  const loading = skillsLoading;
  const error = skillsError;
  const mutationInProgress = createSkillLoading || updateSkillLoading || deleteSkillLoading;

  const handlePrepareCreateSkill = () => {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!skillToEdit) {
      openSidebar();
      setSkillToEdit(null);
    } else {
      toggleSidebar();
    }
  };

  const handlePrepareEditSkill = (skill: SkillWithUsed) => {
    openSidebar();
    setSkillToEdit(skill);
  };

  const handleShowDeleteModal = (skill: SkillWithUsed) => setSkillToDelete(skill);

  const handleCloseDeleteModal = () => setSkillToDelete(null);

  const handleCreate = async (values: Values) => {
    const variables = {
      skill: createSkillFromValues(values),
    };

    try {
      await createSkill({
        variables,
        refetchQueries: [GetSkillsAllWithUsedDocument],
      });

      closeSidebar();
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleUpdate = async (values: Values) => {
    const { id } = values;

    const variables: UpdateSkillMutationVariables = {
      id,
      skill: createSkillFromValues(values),
    };

    try {
      await updateSkill({
        variables,
        update: (cache) => {
          const data = cache.readQuery<GetSkillsAllWithUsedQuery>({
            query: GetSkillsAllWithUsedDocument,
          });

          if (data?.skillsWithUsed) {
            const newSkills = data.skillsWithUsed.map((skill) => {
              if (skill.id === id) {
                return {
                  ...skill,
                  ...createSkillFromValues(values),
                };
              }

              return skill;
            });

            cache.writeQuery<GetSkillsAllWithUsedQuery>({
              query: GetSkillsAllWithUsedDocument,
              data: { skillsWithUsed: newSkills },
            });
          }
        },
      });

      closeSidebar();
      setSkillToEdit(null);
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleDelete = async () => {
    if (!skillToDelete) {
      return;
    }

    const { id } = skillToDelete;

    try {
      await deleteSkill({
        variables: {
          id,
        },
        update: (cache) => {
          const data = cache.readQuery<GetSkillsAllWithUsedQuery>({
            query: GetSkillsAllWithUsedDocument,
          });

          if (data?.skillsWithUsed) {
            const newSkills = data.skillsWithUsed.filter((skill) => skill.id !== id);

            cache.writeQuery<GetSkillsAllWithUsedQuery>({
              query: GetSkillsAllWithUsedDocument,
              data: { skillsWithUsed: newSkills },
            });
          }
        },
      });

      if (skillToEdit && skillToEdit.id === id) {
        closeSidebar();
        setSkillToEdit(null);
      }
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setSkillToDelete(null);
    }
  };

  const handleSubmit = (values: Values) => {
    if (skillToEdit) {
      handleUpdate(values);
    } else {
      handleCreate(values);
    }
  };

  const handleCancel = () => {
    closeSidebar();
    setSkillToEdit(null);
  };

  return (
    <>
      <Header title={t('skill.skills')} testId={TITLE_TAG} />

      <Panel>
        <SkillsHeader
          t={t}
          handleCreate={handlePrepareCreateSkill}
          isCreateButtonActive={isSidebarOpen && !skillToEdit}
        />
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {(loading || mutationInProgress) && <Progress withOverlay />}
        {(() => {
          if (loading || error) {
            return null;
          }

          if (skills.length === 0) {
            return (
              <Center>
                <IllustratedMessage heading={t('skill.noEntries')} icon={<SearchNoneIcon />} />
              </Center>
            );
          }

          return (
            <>
              <SkillsTable
                t={t}
                skills={skills}
                onEditSkill={handlePrepareEditSkill}
                onDeleteSkill={handleShowDeleteModal}
                activeId={skillToEdit ? skillToEdit.id : undefined}
              />

              <ModalConfirmationNewStyle
                acceptCaption={t('layout.action.delete')}
                cancelCaption={t('layout.action.cancel')}
                isOpen={!!skillToDelete}
                onAccept={handleDelete}
                onCancel={handleCloseDeleteModal}
                title={t('skill.delete')}
              >
                <Trans i18nKey="skill.deleteBody" values={{ name: skillToDelete?.name }}>
                  Do you really want to delete chosen skill <em>{skillToDelete?.name}</em>?
                </Trans>
              </ModalConfirmationNewStyle>
            </>
          );
        })()}
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && (
          <SkillsSidebar
            handleCancel={handleCancel}
            handleShowDeleteModal={handleShowDeleteModal}
            handleSubmit={handleSubmit}
            skill={skillToEdit}
            t={t}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
